import {useCallback, useEffect, useRef, useState} from 'react';

import {IEventBaseDataSchema} from '@/services';
import {ErrorManager} from '@/utils/errors/errorManager';
import logError from '@/utils/errors/logError';

import {useLazyGetCardsQuery, useSchedulePhysicalCardMutation} from './api';
import {IFetchCard, ScheduleCardRequest} from './entities';
import {useTrackers} from './trackers';

export const useCardsRepository = () => {
  const [
    triggerGetCards,
    {isSuccess: isGetCardsSuccess, isError: isGetCardsError},
  ] = useLazyGetCardsQuery();
  const [
    triggerSchedulePhysicalCard,
    {
      isSuccess: isSchedulePhysicalCardSuccess,
      isError: isSchedulePhysicalCardError,
    },
  ] = useSchedulePhysicalCardMutation();
  const {
    trackApiErrorRequestCard,
    trackApiTeenAddressPhysicalCardError,
    trackApiTeenAddressPhysicalCardSuccess,
  } = useTrackers();
  //#endregion

  //#region [---- [HOOKS] ----]
  const [cards, setCards] = useState<IFetchCard[]>([]);
  const cardsRef = useRef<IFetchCard[]>([]);

  useEffect(() => {
    // Sync the ref with the current state
    cardsRef.current = cards;
  }, [cards]);

  //#region [---- [LOGIC] ----]
  const handleGetCards = useCallback(async () => {
    try {
      const result = await triggerGetCards().unwrap();
      setCards(result);
    } catch (error) {
      trackApiErrorRequestCard(error);
      logError(error, {
        customTitle: 'Error fetching cards',
        fileName: 'useRepository Cards',
        functionName: 'handleGetCards',
      });
    }
  }, [trackApiErrorRequestCard, triggerGetCards]);

  const handleSchedulePhysicalCard = useCallback(
    async (payload: ScheduleCardRequest, baseSchema: IEventBaseDataSchema) => {
      try {
        await triggerSchedulePhysicalCard(payload).unwrap();
        trackApiTeenAddressPhysicalCardSuccess(baseSchema);
      } catch (error) {
        ErrorManager.handleError(error);
        trackApiTeenAddressPhysicalCardError(baseSchema, error);
        logError(error, {
          customTitle: 'Error scheduling physical card',
          fileName: 'useRepository Cards',
          functionName: 'handleSchedulePhysicalCard',
          data: payload,
        });
      }
    },
    [
      trackApiTeenAddressPhysicalCardError,
      trackApiTeenAddressPhysicalCardSuccess,
      triggerSchedulePhysicalCard,
    ],
  );
  //#endregion

  return {
    cards,
    cardsRef, // Expose cardsRef
    isGetCardsSuccess,
    isGetCardsError,
    isSchedulePhysicalCardSuccess,
    isSchedulePhysicalCardError,
    handleGetCards,
    handleSchedulePhysicalCard,
  };
};
